// Hormone reference ranges and educational content

export const hormoneRanges = {
  fsh: { min: 3.5, max: 12.5, unit: 'mIU/mL', name: 'FSH', fullName: 'Follicle Stimulating Hormone' },
  lh: { min: 2.4, max: 12.6, unit: 'mIU/mL', name: 'LH', fullName: 'Luteinizing Hormone' },
  estradiol: { min: 30, max: 400, unit: 'pg/mL', name: 'E2', fullName: 'Estradiol' },
  amh: { min: 1.0, max: 3.5, unit: 'ng/mL', name: 'AMH', fullName: 'Anti-Müllerian Hormone' },
  inhibin_b: { min: 20, max: 150, unit: 'pg/mL', name: 'Inhibin B', fullName: 'Inhibin B' },
  progesterone: { min: 0.2, max: 25, unit: 'ng/mL', name: 'P4', fullName: 'Progesterone' }
};

export const hormoneEducation = {
  fsh: {
    name: 'FSH (Follicle Stimulating Hormone)',
    description: 'FSH stimulates the growth of ovarian follicles before the release of an egg. As women approach menopause, FSH levels rise because the ovaries become less responsive.',
    significance: 'Elevated FSH (>25 mIU/mL) often indicates diminishing ovarian reserve and approaching menopause.',
    normalRange: '3.5-12.5 mIU/mL (reproductive years)',
    tips: 'FSH levels can fluctuate during perimenopause. Multiple tests over time give a clearer picture.'
  },
  lh: {
    name: 'LH (Luteinizing Hormone)',
    description: 'LH triggers ovulation and helps maintain the corpus luteum. Like FSH, it increases as ovarian function declines.',
    significance: 'The FSH-to-LH ratio can help determine menopausal status. In menopause, both are elevated.',
    normalRange: '2.4-12.6 mIU/mL (reproductive years)',
    tips: 'LH surges mid-cycle during ovulation. Test timing matters.'
  },
  estradiol: {
    name: 'Estradiol (E2)',
    description: 'The primary estrogen produced by the ovaries. It regulates the menstrual cycle, bone health, and many other bodily functions.',
    significance: 'Declining estradiol is a hallmark of menopause and causes many menopausal symptoms.',
    normalRange: '30-400 pg/mL (varies by cycle phase)',
    tips: 'Low estradiol (<30 pg/mL) combined with high FSH strongly suggests menopause.'
  },
  amh: {
    name: 'AMH (Anti-Müllerian Hormone)',
    description: 'Produced by developing follicles, AMH reflects the remaining egg supply (ovarian reserve).',
    significance: 'AMH is the best single marker for ovarian reserve. Very low AMH (<1.0 ng/mL) indicates limited remaining fertility and earlier menopause.',
    normalRange: '1.0-3.5 ng/mL (age-dependent)',
    tips: 'AMH is relatively stable throughout the menstrual cycle, making it reliable for testing anytime.'
  },
  inhibin_b: {
    name: 'Inhibin B',
    description: 'Produced by ovarian follicles, Inhibin B decreases as the pool of follicles diminishes.',
    significance: 'Low Inhibin B (<20 pg/mL) indicates reduced ovarian reserve and is an early marker of declining fertility.',
    normalRange: '20-150 pg/mL',
    tips: 'Inhibin B drops before FSH rises, making it an early indicator of ovarian aging.'
  },
  progesterone: {
    name: 'Progesterone (P4)',
    description: 'Produced after ovulation by the corpus luteum. Essential for maintaining pregnancy.',
    significance: 'During perimenopause, erratic progesterone levels can cause irregular periods and mood changes.',
    normalRange: '0.2-25 ng/mL (varies by cycle phase)',
    tips: 'Progesterone should be tested 7 days after expected ovulation for accurate results.'
  }
};

// Prediction loading messages
export const predictionMessages = [
  { text: "Analyzing biomarker patterns...", icon: 'Dna' },
  { text: "Evaluating hormonal cascade...", icon: 'Flask' },
  { text: "Processing lifestyle modifiers...", icon: 'Heartbeat' },
  { text: "Running predictive algorithm...", icon: 'Brain' },
  { text: "Calculating confidence intervals...", icon: 'Calculator' },
  { text: "Generating clinical insights...", icon: 'Sparkle' },
  { text: "Finalizing analysis...", icon: 'ChartLine' }
];
